import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AppLoader from "../../../components/AppLoader";
import DialogBody from "../../../components/Dialog/Body";
import DialogHeader from "../../../components/Dialog/Header";
import StatusBlockDialog from "../../../components/Dialogs/StatusBlockDialog";
import appcolors from "../../../constants/Colors";
import {
  getCampaignParticipants,
  updateCampaignParticipantStatus,
} from "../../../services/campaignApiFn";
import { handleOpenSnackBar } from "../../../utils";
import SubmissionReviewDialog, {
  submissionStatusColor,
} from "./SubmissionReviewDialog";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "1000px",
    maxWidth: "1000px",
    [theme.breakpoints.down("md")]: {
      margin: 12,
    },
  },
}));

const PARTICIPANT_STATUS = ["pending", "accepted", "rejected"];

const participantStatusColor = (status) => {
  if (status === "pending") return "orange";
  if (status === "accepted") return "green";
  if (status === "rejected") return "red";
};

const CampaignParticipants = forwardRef(({ onUpdated }, ref) => {
  const rejectDialogRef = useRef(null);
  const submissionDialogRef = useRef(null);
  const { t } = useTranslation(["campaign", "common"]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [status, setStatus] = useState("pending");

  const fetchParticipants = async (campaignId, selectedStatus) => {
    try {
      setIsLoading(true);
      const response = await getCampaignParticipants(campaignId, {
        status: selectedStatus,
      });
      if (response?.data?.status === "success") {
        setCampaign(response.data.data.campaign);
        setParticipants(response.data.data.participants || []);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      const initialTab = PARTICIPANT_STATUS.includes(params.tab)
        ? params.tab
        : "pending";
      setCampaign(params.data);
      setStatus(initialTab);
      fetchParticipants(params.data?._id, initialTab);
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setCampaign(null);
    setParticipants([]);
  };

  const handleChangeTab = (_, newValue) => {
    setStatus(newValue);
    fetchParticipants(campaign?._id, newValue);
  };

  const handleUpdateStatus = async ({ participantId, event, reason }) => {
    try {
      setIsLoading(true);
      const payload = { status: event };
      if (reason) payload.reason = reason;
      const response = await updateCampaignParticipantStatus(
        participantId,
        payload,
      );
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        rejectDialogRef.current.closeDialog();
        fetchParticipants(campaign?._id, status);
        onUpdated?.();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRejectDialog = (participant) => {
    rejectDialogRef.current.openDialog({
      type: "Creator",
      status: "Reject",
      data: participant.creator,
      participantId: participant._id,
      event: "rejected",
    });
  };

  const isClosed = campaign?.status === "closed";
  const showVideoColumn = status === "accepted";

  return (
    <React.Fragment>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog onClose={handleClose} open={open}>
        <DialogHeader
          title={t("participants")}
          secondaryHeading={campaign?.name}
          icon={<GroupsOutlinedIcon />}
          onClose={handleClose}
        />
        <DialogBody>
          <Tabs
            value={status}
            onChange={handleChangeTab}
            TabIndicatorProps={{ style: { backgroundColor: "black" } }}
            sx={{
              ".MuiTab-root": { color: "#A6A9AC" },
              ".Mui-selected": { color: "black !important" },
            }}
          >
            {PARTICIPANT_STATUS.map((item) => (
              <Tab key={item} value={item} label={t(item, { ns: "common" })} />
            ))}
          </Tabs>

          <TableContainer sx={{ mt: 2 }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("requestedAt")}</TableCell>
                  <TableCell>{t("creatorDetails", { ns: "common" })}</TableCell>
                  <TableCell>{t("creatorType", { ns: "common" })}</TableCell>
                  <TableCell>{t("socialLinks", { ns: "common" })}</TableCell>
                  <TableCell>{t("status", { ns: "common" })}</TableCell>
                  {showVideoColumn && <TableCell>{t("video")}</TableCell>}
                  <TableCell>{t("actions", { ns: "common" })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {participants.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={showVideoColumn ? 7 : 6}>
                      <Typography fontWeight={500}>
                        {t("noDataFound", { ns: "common" })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  participants.map((participant) => {
                    const creator = participant.creator || {};
                    return (
                      <TableRow key={participant._id}>
                        <TableCell>
                          {moment(participant.createdAt).format("DD/MM/YYYY")}
                          <br />
                          {moment(participant.createdAt).format("hh:mm a")}
                        </TableCell>
                        <TableCell>
                          <Stack
                            gap={1.5}
                            direction={"row"}
                            alignItems={"center"}
                          >
                            <Avatar
                              src={creator.profileImg}
                              sx={{ height: 48, width: 48 }}
                            >
                              {!creator.profileImg && creator.username
                                ? creator.username.charAt(0).toUpperCase()
                                : null}
                            </Avatar>
                            <Stack>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                color={appcolors.black}
                                textTransform={"capitalize"}
                              >
                                {creator.username || "__"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color={appcolors.gunmetal}
                              >
                                {creator.email}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {creator.creatorType || "__"}
                        </TableCell>
                        <TableCell>
                          <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
                            {Object.entries(creator.socialLinks || {})
                              .filter(([, link]) => link)
                              .map(([platform, link]) => (
                                <Chip
                                  key={platform}
                                  label={platform}
                                  size="small"
                                  component="a"
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  clickable
                                  sx={{ textTransform: "capitalize" }}
                                />
                              ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={participantStatusColor(participant.status)}
                          >
                            {t(participant.status, { ns: "common" })}
                          </Typography>
                          {participant.status === "rejected" &&
                            participant.rejectionReason && (
                              <Typography
                                variant="caption"
                                color={"text.secondary"}
                              >
                                {participant.rejectionReason}
                              </Typography>
                            )}
                        </TableCell>
                        {showVideoColumn && (
                          <TableCell>
                            {participant.submission ? (
                              <Stack gap={0.75} alignItems={"flex-start"}>
                                <Chip
                                  size="small"
                                  color={submissionStatusColor(
                                    participant.submission.status
                                  )}
                                  label={t(
                                    `submissionStatus.${participant.submission.status}`
                                  )}
                                />
                                <Button
                                  size="small"
                                  variant={
                                    participant.submission.status ===
                                    "submitted"
                                      ? "contained"
                                      : "text"
                                  }
                                  onClick={() =>
                                    submissionDialogRef.current.openDialog({
                                      data: participant,
                                    })
                                  }
                                >
                                  {participant.submission.status ===
                                  "submitted"
                                    ? t("reviewVideo")
                                    : t("viewVideo")}
                                </Button>
                              </Stack>
                            ) : (
                              <Typography
                                variant="body2"
                                color={"text.secondary"}
                              >
                                {t("submissionStatus.notSubmitted")}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Stack direction={"row"} gap={1}>
                            {participant.status !== "accepted" && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={isClosed}
                                onClick={() =>
                                  handleUpdateStatus({
                                    participantId: participant._id,
                                    event: "accepted",
                                  })
                                }
                              >
                                {t("accept")}
                              </Button>
                            )}
                            {participant.status !== "rejected" && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={isClosed}
                                onClick={() =>
                                  handleOpenRejectDialog(participant)
                                }
                              >
                                {t("reject")}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogBody>
      </BootstrapDialog>
      <StatusBlockDialog ref={rejectDialogRef} onConfirm={handleUpdateStatus} />
      <SubmissionReviewDialog
        ref={submissionDialogRef}
        onReviewed={() => {
          fetchParticipants(campaign?._id, status);
          onUpdated?.();
        }}
      />
    </React.Fragment>
  );
});

export default CampaignParticipants;
